export type BatchStatus = "queued" | "running" | "scheduled" | "paused" | "completed" | "failed" | "cancelled";
export type BatchActionStatus = BatchStatus | "resume";

/** Board follows the batch lifecycle; failed/cancelled remain archive-only legacy states. */
export const BOARD_COLUMNS: { id: BatchStatus; label: string }[] = [
  { id: "queued", label: "Queued" },
  /* Board label only -- the `running` id stays as-is so the API contract and
     every status query keep working. A batch lands here while a live transcript
     is streaming, i.e. it is actively on a call. */
  { id: "running", label: "Dialing" },
  { id: "scheduled", label: "Scheduled" },
  { id: "paused", label: "Paused" },
  { id: "completed", label: "Completed · 7d" },
];

/** Workflow View Runs: only Queued + Completed. Running batches sit in Queued. */
export const WORKFLOW_BOARD_COLUMNS: { id: BatchStatus; label: string }[] = [
  { id: "queued", label: "Running" },
  { id: "completed", label: "Completed · 7d" },
];

export const WORKFLOW_COLUMN_STATUSES: Partial<Record<BatchStatus, BatchStatus[]>> = {
  queued: ["queued", "running"],
  completed: ["completed"],
};

export interface BatchError { code: string; message: string; at: string }
export interface LiveCallSummary {
  call_id: string;
  schedule_id: string;
  contact_id: string;
  contact_name?: string;
  customer_name?: string;
  customer_phone?: string;
  agent_name?: string;
  message_count: number;
  created_at?: string;
  updated_at?: string;
  last_message_at?: string;
  status: string;
}
export interface LiveTranscriptMessage {
  index: number;
  role: "assistant" | "user";
  text: string;
  interrupted: boolean;
  created_at?: string;
  item_id?: string;
}
export interface LiveTranscriptDetail extends LiveCallSummary {
  messages: LiveTranscriptMessage[];
}
export interface LiveCallsResponse {
  schedule_id: string;
  schedule_status: string;
  items: LiveCallSummary[];
  total: number;
}
export interface Batch {
  schedule_id: string; name: string; language: "HI" | "EN"; status: BatchStatus;
  total: number; completed: number; running: number; retry: number; callback: number; failed?: number; cancelled?: number;
  accepted?: number; rejected?: number;
  accepted_contacts?: number; rejected_contacts?: number;
  concurrency: number; agent: string; version: string;
  asr?: number; mos?: number; pdd?: number; drop?: number;
  connectivity?: number;
  error?: BatchError; finished_at?: string; truncated?: boolean; column_total?: number;
  active_calls?: LiveCallSummary[]; active_calls_count?: number;
  /** True when a callback / primary call / PCA is still pending: date filters must not hide it. */
  has_pending_work?: boolean;
  schedule_date?: string; schedule_time?: string; schedule_timezone?: string;
  created_at?: string; updated_at?: string; completed_at?: string; status_at?: string;
  resume_status?: "running" | "scheduled";
}
export interface AttentionCounts { failed: number; stalled: number }

/** One row of a batch's contact list, as returned by GET /api/v1/schedules/{id}. */
export interface BatchContact {
  contact_id?: string;
  name?: string;
  phone_number?: string;
  status?: string;
  rejection_reason?: string;
  metadata?: Record<string, unknown>;
  last_error?: string;
}

/** Detail payload for a single schedule; only the contact arrays are used here. */
export interface BatchDetail {
  schedule_id: string;
  contacts?: BatchContact[];
  rejected_contacts_data?: BatchContact[];
}

export interface BatchCallback {
  call_id?: string;
  contact_id?: string;
  contact_name?: string;
  customer_phone?: string;
  status?: string;
  contact_status?: string;
  callback_date?: string;
  callback_time?: string;
  callback_timezone?: string;
}

export interface BatchCallbacksResponse {
  schedule_id: string;
  items?: BatchCallback[];
  callbacks?: BatchCallback[];
  total?: number;
}

export const STATUS_META: Record<BatchStatus, { dot: string; spine: string }> = {
  queued:    { dot: "#2563eb", spine: "#2563eb" },
  running:   { dot: "#4ade80", spine: "#ff8a4c" },
  scheduled: { dot: "#d97706", spine: "#d97706" },
  paused:    { dot: "#d99418", spine: "#d99418" },
  completed: { dot: "#2b8f52", spine: "#2b8f52" },
  failed:    { dot: "#d92d20", spine: "#d92d20" },
  cancelled: { dot: "#a8a099", spine: "#a8a099" },
};
